<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { UIChevronLeftIocn, UIChevronRightIocn } from '../icons/index'
import UIEventsPopover from '../popover/popover.vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { EVENT_VIEWS } from '@modules/events/domain/data/view'
import { getDateString } from '@modules/events/domain/utils/date'

export default {
  name: 'ui-events-calendar',
  components: {
    'full-calendar': FullCalendar,
    'ui-chevron-left-icon-cmp': UIChevronLeftIocn,
    'ui-chevron-right-icon-cmp': UIChevronRightIocn,
    'ui-events-popover': UIEventsPopover,
  },
  computed: {
    ...mapState('events', {
      isLoading: state => state.isLoading,
    }),
    ...mapGetters('events', {
      events: 'getCalendarEvents',
    }),
    calendarApi() {
      return this.$refs.calendarRef.getApi()
    },
    caledarBaseOptions() {
      return {
        headerToolbar: {
          left: 'prev',
          center: 'title',
          right: 'next',
        },
        plugins: [dayGridPlugin, interactionPlugin],
        dayMaxEvents: true,
        initialView: 'dayGridMonth',
        locale: 'ru',
        firstDay: 1,
        progressiveEventRendering: true,
      }
    },
  },
  methods: {
    ...mapActions('modal', {
      modalClose: 'modalClose',
    }),
    ...mapActions('events', {
      events_setPopoverOpened: 'setPopoverOpened',
      events_setPopoverClosed: 'setPopoverClosed',
    }),
    async handleDatesSet({ start, end, view }) {
      const startDate = getDateString(start)
      const endDate = getDateString(end)

      const query = {
        ...this.$route.query,
        view: EVENT_VIEWS.CALENDAR,
        date: getDateString(view.currentStart),
        startDate,
        endDate,
      }

      if (
        this.$route.query.date === query.date &&
        this.$route.query.startDate === query.startDate &&
        this.$route.query.endDate === query.endDate
      ) {
        return
      }

      this.$router.replace({ query })
    },
    handleToolbarDirClick(dir) {
      return () => {
        this.calendarApi[dir]()
      }
    },
    getCalendarTitle(args) {
      const monthNames = [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь',
      ]
      const { year, month } = args.date

      return `${monthNames[month]} ${year}`
    },
    getMoreLinkContent(arg) {
      let eventEl = document.createElement('span')

      eventEl.innerText = `еще ${arg.num} события`
      return { domNodes: [eventEl] }
    },
    getEventContent({ event, ...args }) {
      return `${event.title}`
    },
    handlePopoverOpen(event) {
      this.isPopoverOpened = true
      const x = Math.min(event.pageX, window.innerWidth - 460)
      this.popoverStyles = 'top: ' + event.pageY + 'px; left: ' + x + 'px;'
    },
    getPopoverStyles(event) {
      const x = Math.min(event.pageX, window.innerWidth - 460)
      return 'top: ' + event.pageY + 'px; left: ' + x + 'px;'
    },
    getEventObject(event) {
      return {
        start: event.start,
        end: event.end,
        title: event.title,
        extendedProps: event.extendedProps,
      }
    },
    handleEventClick({ event, jsEvent }) {
      const style = this.getPopoverStyles(jsEvent)
      const events = [this.getEventObject(event)]
      this.events_setPopoverOpened({ events, style })
    },
    handleMoreClick({ hiddenSegs, jsEvent }) {
      const style = this.getPopoverStyles(jsEvent)
      const events = hiddenSegs.map(item => {
        return this.getEventObject(item.event)
      })

      this.events_setPopoverOpened({ events, style })

      // возвращаем пустой объект чтобы не отрисовывался дефолтный поповер
      return {}
    },
  },
}
</script>

<template>
  <div class="events-calendar">
    <div class="events-calendar__loader" v-if="isLoading" />
    <full-calendar
      ref="calendarRef"
      :options="{
        ...caledarBaseOptions,
        customButtons: {
          prev: { click: handleToolbarDirClick('prev') },
          next: { click: handleToolbarDirClick('next') },
        },
        events,
        datesSet: handleDatesSet,
        initialDate: this.$route.query.date || new Date(),
        windowResize: events_setPopoverClosed,
        titleFormat: getCalendarTitle,
        moreLinkContent: getMoreLinkContent,
        moreLinkClick: handleMoreClick,
        eventContent: getEventContent,
        eventBackgroundColor: 'green',
        eventClick: handleEventClick,
      }"
    />
  </div>
</template>

<style lang="sass">
.events-calendar
  min-width: 375px
  position: relative
  z-index: 0

  @media screen and (max-width: 920px)
    padding-inline: 16px

  &__loader
    position: absolute
    inset: -12px
    inset-block-start: 60px
    border-radius: 4px
    z-index: 2
    background: rgba(0,0,0, 0.01)
    cursor: wait
    backdrop-filter: blur(2px)

  .fc .fc-daygrid-day-frame
    min-height: auto
    position: absolute
    inset: 3px


  .fc-daygrid-dot-event
    background: #00b0bd
    color: white

  .fc-toolbar-chunk
    button
      display: flex
      height: 48px
      width: 48px
      margin: 0
      padding: 0
      align-items: center
      justify-content: center
      color: #929292
      background: transparent
      border: none

      .fc-icon
        display: inherit
        font-size: 2em

      &:hover
        background: #00a8e8
        color: white

      &:active
        background: #00a8e8 !important
        color: white !important

  .fc-daygrid-body
    z-index: 0

  @media screen and (max-width: 580px)
    .fc-scrollgrid .fc-scroller-liquid-absolute
      overflow: hidden !important

  table[role="grid"]
    border: none



  th[role="columnheader"]
    border: none
    padding-block-end: 16px

    .fc-scrollgrid-sync-inner
      display: flex
      align-items: center
      justify-content: center
      height: 48px
      background-color: #ededed
      color: #5b5b5b
      font-size: 16px
      text-transform: uppercase
      font-family: 'Segoe UI', sans-serif
      font-weight: 600

    &.fc-day-sat,
    &.fc-day-sun
      .fc-scrollgrid-sync-inner
        background: #e8e8e8
        color: #5b5b5b


  .fc-theme-standard td,
  .fc-theme-standard th
    border: none

  td[role="gridcell"]
    padding: 2px
    border: none
    position: relative

    &.fc-day-today
      background-color: transparent

      .fc-scrollgrid-sync-inner
        box-shadow: 0 0 0 1px #00a8e8

    .fc-daygrid-day-top
      padding-block: 24px 8px
      padding-inline: 24px
      flex-direction: row
      font-size: 28px
      line-height: 34px
      font-family: Proxima\\A0Nova Semi, sans-serif
      color: #929292

      @media screen and (min-width: 1640px)
        font-size: 40px

    &.fc-day-sat,
    &.fc-day-sun
      .fc-daygrid-day-top
        color: #ffbfb0

    .fc-scrollgrid-sync-inner
      background: white
      border-radius: 4px

    .fc-event-main,
    .fc-event
      overflow: hidden
      text-overflow: ellipsis
      cursor: pointer

    .fc-event
      margin-inline: 4px
      padding-inline: 4px

    .fc-more-link
      height: 28px
      width: 100%
      display: flex
      align-items: center
      justify-content: center

      @media screen and (min-width: 920px)
        &:hover
          background: #00b0bd
          color: white
          transition-property: color, background
          transition-duration: 200ms

    @media screen and (max-width: 920px)
      .fc-scroller
        overflow: hidden !important

      .fc-daygrid-event-harness
        height: 150px

      .fc-daygrid-day-events
        inset: 0
        margin: 0

      .fc-more-link
        position: absolute
        inset: 0
        padding: 0
        margin: 0
        display: flex
        height: 100%
        content: ""
        border-bottom: 3px solid #00a8e8
        border-radius: 4px

        span
          visibility: hidden

      .fc-scrollgrid-sync-inner
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center

      .fc-daygrid-day-top
        align-items: center
        justify-content: center
        flex-grow: 1
        font-size: 30px
        padding: 0

    @media screen and (max-width: 580px)
      .fc-daygrid-day-top
        font-size: 18px
</style>



// WEBPACK FOOTER //
// src/templates/pages/events/components/events-calendar/events-calendar.vue